import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from '../../Services/firebase-config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const formatNumberWithCommas = (value) => {
  if (value === null || value === undefined || value === '') return '0';
  const number = parseFloat(value);
  if (isNaN(number)) return value.toString();
  return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const EstablishmentCosts = () => {
  const [tableData, setTableData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    costCenter: '',
    unit: '',
    quantity: '',
    unitCost: '',
  });
  const [editIndex, setEditIndex] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [kibaloName, setKibaloName] = useState('');
  const [establishmentYears, setEstablishmentYears] = useState('');
  const [establishmentCostPerYear, setEstablishmentCostPerYear] = useState(0);
  const [basisOfCosting, setBasisOfCosting] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { kibaloId } = useParams();
  const cleanKibaloId = kibaloId ? kibaloId.split('/')[0] : kibaloId;

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchKibaloData = async () => {
      if (!user || !cleanKibaloId) return;

      const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
      const kibaloSnap = await getDoc(kibaloRef);
      if (kibaloSnap.exists()) {
        const data = kibaloSnap.data();
        setKibaloName(data.name || '');
        setEstablishmentYears(data.establishmentYears || '');
        setEstablishmentCostPerYear(data.establishmentCostPerYear || 0);

        const costingBasis = data.costingBasis;
        if (costingBasis) {
          const basisText = costingBasis.costBasis === 'year'
            ? `Production per year: ${formatNumberWithCommas(costingBasis.quantity)} ${costingBasis.unit}`
            : `Production per acreage: (${formatNumberWithCommas(costingBasis.quantity)} acres, ${formatNumberWithCommas(costingBasis.productionPerAcre)} ${costingBasis.unit}/year)`;

          setBasisOfCosting(basisText);
        }
      }
    };

    fetchKibaloData();
  }, [user, cleanKibaloId]);

  const fetchData = useCallback(async () => {
    if (!user || !cleanKibaloId) return;

    const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
    const docSnap = await getDoc(kibaloRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      setTableData(data.establishmentTable || []);
    }
  }, [user, cleanKibaloId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const calculateTotalCost = () =>
    tableData.reduce((total, row) => total + (parseFloat(row.amount) || 0), 0).toFixed(2);

  const handleModalSubmit = async () => {
    if (!user || !cleanKibaloId) return;

    const { costCenter, unit, quantity, unitCost } = formData;
    if (!costCenter || !unit || !quantity || !unitCost) return;

    const amount = (Number(quantity) * Number(unitCost)).toFixed(2);
    const newRow = { costCenter, unit, quantity, unitCost, amount };

    let updatedTableData;
    const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);

    if (editIndex !== null) {
      updatedTableData = tableData.map((row, index) => index === editIndex ? newRow : row);
    } else {
      updatedTableData = [...tableData, newRow];
    }

    await updateDoc(kibaloRef, { establishmentTable: updatedTableData });
    setTableData(updatedTableData);

    setFormData({ costCenter: '', unit: '', quantity: '', unitCost: '' });
    setShowModal(false);
    setEditIndex(null);
  };

  const handleEditRow = (index) => {
    setFormData(tableData[index]);
    setEditIndex(index);
    setShowModal(true);
  };

  const handleDeleteRow = async (index) => {
    setItemToDelete(index);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete === null) return;
    const updatedTableData = tableData.filter((_, i) => i !== itemToDelete);
    const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
    await updateDoc(kibaloRef, { establishmentTable: updatedTableData });
    setTableData(updatedTableData);
    setShowDeleteModal(false);
    setItemToDelete(null);
  };


  const handleCalculateEstablishmentCostPerYear = async () => {
    const years = parseFloat(establishmentYears);
    if (!years || years <= 0) {
        alert("Please enter a valid number of years greater than 0.");
        return;
    }
    const totalCost = parseFloat(calculateTotalCost());
    const costPerYear = totalCost / years;
    setEstablishmentCostPerYear(costPerYear);

    if (user && cleanKibaloId) {
        const kibaloRef = doc(db, `users/${user.uid}/kibalos/${cleanKibaloId}`);
        await updateDoc(kibaloRef, {
            establishmentYears: years,
            establishmentCostPerYear: costPerYear
        });
    }
};

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-xl p-8 space-y-6 border border-gray-200">

        {/* HEADER */}
        <div className="border-b pb-4">
          <h1 className="text-2xl font-bold text-gray-800 uppercase tracking-wide">
            ESTABLISHMENT COSTS FOR {kibaloName} ENTERPRISE (For Perennial Crops)
          </h1>
          <p className="text-md text-gray-600 mt-1 font-medium">{basisOfCosting}</p>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700 uppercase text-xs font-semibold">
              <tr>
                <th className="border px-4 py-3 text-left">Cost Center</th>
                <th className="border px-4 py-3 text-left">Unit</th>
                <th className="border px-4 py-3 text-left">Quantity</th>
                <th className="border px-4 py-3 text-left">Unit Cost</th>
                <th className="border px-4 py-3 text-left">Amount</th>
                <th className="border px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {tableData.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-6 text-gray-500">
                    No establishment costs added yet.
                  </td>
                </tr>
              )}

              {tableData.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50 transition">
                  <td className="border px-4 py-3">{row.costCenter}</td>
                  <td className="border px-4 py-3">{row.unit}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(row.quantity)}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(row.unitCost)}</td>
                  <td className="border px-4 py-3">{formatNumberWithCommas(row.amount)}</td>

                  <td className="border px-4 py-2 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEditRow(index)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded-md text-xs hover:bg-yellow-600 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRow(index)}
                        className="px-3 py-1 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* TOTAL ROW */}
              <tr className="bg-gray-100 font-semibold">
                <td colSpan="4" className="border px-4 py-3 text-right">Total Cost</td>
                <td className="border px-4 py-3">{formatNumberWithCommas(calculateTotalCost())}</td>
                <td className="border px-4 py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ---------- ESTABLISHMENT COST PER YEAR ---------- */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg shadow-inner border border-blue-100">
            <h2 className="font-bold text-lg mb-2 text-blue-800">Number of Years/Harvests The Perenial Crop Will Last</h2>
            <div className="flex flex-col gap-2">
                <input
                    type="number"
                    value={establishmentYears}
                    onChange={e => setEstablishmentYears(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-400 focus:outline-none"
                    placeholder="Enter number of years the establishment will serve"
                    min="0.01"
                    step="any"
                />
                <button
                    onClick={handleCalculateEstablishmentCostPerYear}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    Calculate Establishment Cost Per Year
                </button>
            </div>
            <div className="mt-4 p-3 bg-white border border-gray-200 rounded-md shadow-sm text-center">
                <p className="text-gray-600 font-semibold">Establishment Cost Per Year</p>
                <p className="text-xl font-bold text-blue-600 mt-1">
                    {establishmentCostPerYear ? formatNumberWithCommas(establishmentCostPerYear) : '-'}
                </p>
            </div>
        </div>

        {/* NAVIGATION BUTTONS */}
        <div className="flex flex-wrap justify-between gap-2 pt-4 border-t">
          <button
            onClick={() => navigate(`/basic-costing/${cleanKibaloId}`)}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg shadow"
          >
            Back to Basis of Costing
          </button>

          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow"
          >
            Add Establishment Cost
          </button>

          <button
            onClick={() => navigate(`/equipment-costing/${cleanKibaloId}`)}
            className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg shadow"
          >
            Go to Depreciation
          </button>
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl">
            <h2 className="text-xl font-semibold mb-4 border-b pb-2">
              {editIndex !== null ? 'Edit Establishment Cost' : 'Add Establishment Cost'}
            </h2>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Cost Center"
                value={formData.costCenter}
                onChange={(e) => setFormData({ ...formData, costCenter: e.target.value })}
                className="w-full border px-3 py-2 rounded-lg focus:ring focus:outline-none"
              />

              <input
                type="text"
                placeholder="Unit"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full border px-3 py-2 rounded-lg focus:ring focus:outline-none"
              />

              <input
                type="number"
                placeholder="Quantity"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full border px-3 py-2 rounded-lg focus:ring focus:outline-none"
              />

              <input
                type="number"
                placeholder="Unit Cost"
                value={formData.unitCost}
                onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                className="w-full border px-3 py-2 rounded-lg focus:ring focus:outline-none"
              />
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditIndex(null);
                  setFormData({ costCenter: '', unit: '', quantity: '', unitCost: '' });
                }}
                className="px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500"
              >
                Cancel
              </button>

              <button
                onClick={handleModalSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editIndex !== null ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-1/3">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p>Are you sure you want to delete this item?</p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="ml-2 bg-gray-300 text-gray-700 px-4 py-2 rounded-md shadow hover:bg-gray-400"
              >
                Cancel
              </button>
              <button onClick={confirmDelete} className="bg-red-500 text-white px-4 py-2 rounded-md shadow hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstablishmentCosts;
